import React, { useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { clsx } from 'clsx'
import { Edit, FileDownload, TrashDelete, OverflowMenu } from '@/assets/icons'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectAgentConversationFromId } from '@/agent/conversations/store/selectors'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { downloadAgentConversationAsDocx } from '@/agent/conversations/util/DownloadAgentConversation'
import { useIsConversationOwner } from '@/agent/conversations/hooks/useIsConversationOwner'
import { ChatListSidebarItem } from '../conversation_list/schema'
import { ChatOverflowMenuItem } from './ChatOverflowMenuItem'
import { DEVICE_TYPE, useDeviceType } from '@/hooks/useDeviceType'
import { MatterSubmenu } from './MatterSubMenu'
import { MobileMatterSubMenuContent } from './MobileMatterSubMenuContent'
import { useNavigate, useParams } from 'react-router-dom'
import { buildChatFeaturePath, buildConversationPath } from '../conversation-navigation/util/conversation-path-builders'
import { setMobileChatHistoryOpen } from '@/store/slices/ui-state.slice'
import useAssignConversationMatter from '@/matter/hooks/useAssignConversationMatter'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs, kToastDurationMediumMs } from '@/constants/constants-ui'
import { kUntitledMatterLabel } from '@/matter/constants'

type ChatOverflowMenuProps = {
  chatListSidebarItem: ChatListSidebarItem
  onSelectRename: () => void
  onSelectDelete: () => void
  onOpenChange?: (open: boolean) => void
  open?: boolean
  iconSize?: number
  iconColorClass?: string
  showTriggerOnHover?: boolean
  isRowActive?: boolean
}

/**
 * Chat conversation overflow menu with delete, rename, download, and matter assignment actions.
 * Supports mobile/desktop UI variants.
 */
export const ChatOverflowMenu: React.FC<ChatOverflowMenuProps> = (props: ChatOverflowMenuProps) => {
  const {
    chatListSidebarItem,
    onSelectRename,
    onSelectDelete,
    iconSize = 4,
    iconColorClass,
    showTriggerOnHover = false,
    onOpenChange,
    open,
    isRowActive = false,
  } = props

  // Mobile matters content open state
  const [mobileMattersContentOpen, setMobileMattersContentOpen] = useState(false)

  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const { chatId: activeChatId } = useParams()
  const navigate = useNavigate()

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)

  const agentConversation = useAppSelector((state: RootState) => selectAgentConversationFromId(state, { conversationId: chatListSidebarItem.id }))

  const conversationMatterId = agentConversation?.matter_id ?? null

  // Non-owners (collaborators viewing a chat they didn't create) get read-only options: Download only.
  const isOwner = useIsConversationOwner(chatListSidebarItem.id)

  // Device type to determine if the menu overflow is on mobile or desktop
  const deviceType = useDeviceType()
  const isMobile = deviceType === DEVICE_TYPE.MOBILE

  // Hook for matter assignment
  const { assignMatter } = useAssignConversationMatter({ conversationId: chatListSidebarItem.id ?? '' })

  // Menu elements. Delete and Rename mutate the chat, so they're owner-only; Download is always available.
  const menuElements = [
    ...(isOwner
      ? [
          {
            hideFromAnon: true,
            name: 'Delete',
            icon: TrashDelete,
            destructive: true,
            onClick: () => {
              trackEvent(AnalyticsEvent.OpenConversationDeletionDialog)
              onSelectDelete()
            },
          },
          {
            name: 'Rename',
            icon: Edit,
            onClick: () => {
              trackEvent(AnalyticsEvent.OpenConversationRetitleDialog)
              onSelectRename()
            },
          },
        ]
      : []),
    {
      name: 'Download',
      icon: FileDownload,
      onClick: () => {
        trackEvent(AnalyticsEvent.DownloadConversationToDocx)
        if (agentConversation) {
          downloadAgentConversationAsDocx(agentConversation)
        }
      },
    },
  ]

  /**
   * Handle matter selection for conversation assignment
   */
  const handleMatterSelect = async (toMatterId: string, toMatterName?: string) => {
    if (toMatterId === conversationMatterId) {
      return
    }

    // Analytics
    trackEvent(AnalyticsEvent.ConversationMatterChanged, {
      conversationId: chatListSidebarItem.id,
      fromMatterId: conversationMatterId || '',
      toMatterId: toMatterId || '',
    })

    try {
      await assignMatter(toMatterId, () => {
        // If the conversation is the active chat and the active matter is not null, navigate to the active matter
        if (chatListSidebarItem.id === activeChatId && activeMatterId !== null) {
          navigate(buildChatFeaturePath(activeMatterId))
        }

        onOpenChange?.(false)

        // Close the chat list sidebar on mobile
        if (isMobile) {
          dispatch(setMobileChatHistoryOpen(false))
        }

        dispatch(
          openGlobalToast({
            type: GlobalToastType.SUCCESS,
            message: `Conversation moved to ${toMatterName ?? kUntitledMatterLabel}`,
            durationMs: kToastDurationMediumMs,
            withLink: { label: 'Go to Chat', href: buildConversationPath(toMatterId, chatListSidebarItem.id) },
            truncateAtLine: 2,
          })
        )
      })
    } catch (err) {
      console.error('Error while moving conversation:', err)
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Error while moving conversation', durationMs: kToastDurationShortMs }))

      // Reconcile the source and destination matters with the server after a failed move.
      if (agentConversation) {
        dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId: agentConversation.matter_id, forceRefresh: true }))
        dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId: toMatterId, forceRefresh: true }))
      }
    }
  }

  return (
    <>
      <DropdownMenu.Root open={open} onOpenChange={onOpenChange}>
        <DropdownMenu.Trigger asChild>
          <button
            className={clsx(
              'flex items-center justify-center outline-none rounded-full p-1 transition-all duration-200 ease-in-out hover:bg-brand-neutral-200',
              showTriggerOnHover
                ? clsx('lg:opacity-0 lg:group-hover:opacity-100 data-[state=open]:opacity-100', isRowActive && 'lg:opacity-100')
                : 'opacity-100'
            )}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            <OverflowMenu className={clsx(`h-${iconSize} w-${iconSize}`, iconColorClass)} />
          </button>
        </DropdownMenu.Trigger>
        {/* Matters content for mobile - to replace the content of the dropdown menu */}
        {mobileMattersContentOpen ? (
          <MobileMatterSubMenuContent
            setMattersContentOpen={setMobileMattersContentOpen}
            fromMatterId={conversationMatterId || null}
            handleMatterSelect={handleMatterSelect}
          />
        ) : (
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="z-50 min-w-[130px] bg-white rounded-lg p-2 border shadow-[0_0_6px_-2px_rgba(0,_0,_0,_0.05),_0_0_15px_-3px_rgba(0,_0,_0,_0.10)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
              align="end"
              sideOffset={3}
            >
              {/* Menu elements */}
              <ul role="list" className="flex flex-col gap-2">
                {menuElements.map((item) => (
                  <li key={item.name} role="none">
                    <ChatOverflowMenuItem name={item.name} icon={item.icon} onClick={item.onClick} destructive={item.destructive} />
                  </li>
                ))}
                {isOwner && (
                  <MatterSubmenu
                    isMobile={isMobile}
                    mattersContentOpen={mobileMattersContentOpen}
                    setMattersContentOpen={setMobileMattersContentOpen}
                    fromMatterId={conversationMatterId || null}
                    handleMatterSelect={handleMatterSelect}
                  />
                )}
              </ul>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        )}
      </DropdownMenu.Root>
    </>
  )
}
